import { useScaffold } from "@Light/scaffold";
import clsx from "clsx";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useOverlay } from "../Overlay";
import { BackButton } from "./BackButton";
import { HeaderContext, OnBack } from "./context";
import { useDefaultHeaderTitle } from "./menu/MenuHeaderTitle";

export type WithHeaderProps = {
  children: React.ReactNode;
};

export function WithHeader({ children }: WithHeaderProps) {
  const [onBack, setOnBack] = useState<OnBack | undefined>(undefined);
  const parentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );
  const [divider, setDivider] = useState(true);
  const [titleText, setTitleText] = useState<React.ReactNode | undefined>(
    undefined,
  );
  const [leftButton, setLeftButton] = useState<React.ReactNode | undefined>(
    undefined,
  );
  const [rightButton, setRightButton] = useState<React.ReactNode | undefined>(
    undefined,
  );
  const [floating, setFloating] = useState(false);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const parent = parentRef.current;
    const header = headerRef.current;
    if (!parent || !header) {
      return;
    }

    const updateContentHeight = () => {
      const siblingHeight = floating ? 0 : header.offsetHeight;
      setContentHeight(parent.offsetHeight - siblingHeight);
    };

    const observer = new ResizeObserver(updateContentHeight);
    observer.observe(parent);
    observer.observe(header);
    return () => {
      observer.disconnect();
    };
  }, [floating]);

  const scaffold = useScaffold();
  const { contentOpacity } = useOverlay();
  const { isMinimalHeader } = scaffold.page.header;
  const Header = scaffold.page.header.header;
  const defaultTitle = useDefaultHeaderTitle();
  const hasTitle = Boolean(titleText ?? defaultTitle);
  const hasDivider = divider && !floating && hasTitle;
  return (
    <HeaderContext.Provider
      value={{
        onBack,
        setOnBack,
        contentHeight,
        setContentHeight,
        divider,
        setDivider,
        titleText,
        setTitleText,
        leftButton,
        setLeftButton,
        rightButton,
        setRightButton,
        floating,
        setFloating,
        compact,
        setCompact,
      }}
    >
      <div className="relative flex flex-col w-full h-full" ref={parentRef}>
        <Header
          onBack={onBack}
          ref={headerRef}
          titleLink="/"
          leftButton={leftButton}
          rightButton={rightButton}
          floating={floating}
          compact={compact}
        >
          {titleText}
        </Header>
        <motion.div
          className={clsx(
            "h-full",
            hasDivider && "border-t",
            isMinimalHeader && "sm:border-t-0",
          )}
          style={contentOpacity ? { opacity: contentOpacity } : undefined}
        >
          {children}
        </motion.div>
      </div>
    </HeaderContext.Provider>
  );
}

export type HeaderProps = React.ComponentProps<"header"> & {
  onBack: OnBack | undefined;
  titleLink?: string;
  leftButton?: React.ReactNode;
  rightButton?: React.ReactNode;
  floating?: boolean;
  compact?: boolean;
};

export const Header: React.FC<HeaderProps> = React.forwardRef(function Header(
  {
    onBack,
    titleLink,
    leftButton,
    rightButton,
    floating,
    compact,
    ...props
  }: HeaderProps,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const scaffold = useScaffold();
  const MenuHeader = scaffold.page.header.menu.menuHeader;
  const DefaultRightButton = scaffold.page.header.defaultRightButton;
  return (
    <MenuHeader
      {...props}
      ref={ref}
      titleLink={titleLink}
      leftButton={
        leftButton ?? (onBack ? <BackButton onBack={onBack} /> : undefined)
      }
      rightButton={rightButton ?? <DefaultRightButton />}
      floating={floating}
      compact={compact}
    />
  );
});
